import { BooleanValue } from "../../../../components/ds2/BooleanValue";
import Icon from "../../../../components/ui/Icon";
import {
  AddIcon,
  AsyncSelect,
  Button,
  FlexContent,
  Form,
  FormField,
  InlineOrderEditor,
  ModalDialog,
  Numeric,
  Switch,
  TableCell,
  TableHeadCell,
  TableRow,
} from "@rothoblaas/design-system";
import { FC, useContext, useEffect, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import FormFieldsContainer from "../../../../components/ui/FormFieldsContainer";
import Table from "../../../../components/ui/Table";
import { getDeleteAction, getEditAction } from "../../../../utils/rowActions";
import Visibility from "../../../../components/ui/Visibility";
import VisibilityScope from "../../../../components/ui/VisibilityScope";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../../../context/notificationContext";
import useInitializer from "../../../../hooks/useInitializer";
import useListFilters from "../../../../hooks/useListFilters";
import useI18n from "../../../../hooks/useTranslations";
import { AdminLockStop } from "../../../../http/lock_calculator";
import { useAdminLockCalculatorApi } from "../../../../http/lock_calculator/useAdminLockCalculator";
import { SearchAdminItem } from "../../../../http/types/items";
import { useItemsApi } from "../../../../http/useItems";
import { PaginationQuery } from "../../../../types";

type ItemOption = Pick<SearchAdminItem, "id" | "code" | "description">;

interface FormBody {
  item: ItemOption | null;
  fyK: number | null;
  area: number | null;
  fLockstopRk: number | null;
  isSingle: boolean;
  visibility: "public" | "internal" | "hidden" | null;
}

const AdminLockStopsTab: FC = () => {
  const i18n = useI18n();
  const adminApi = useAdminLockCalculatorApi();
  const dispatch = useContext(NotificationDispatch);
  const filters = useListFilters({ isSearchable: true, prefix: "lockStops" });
  const aborter = useRef(new AbortController());
  const debouncedValue = filters.state.q;
  const query: PaginationQuery = { ...filters.state, q: debouncedValue };
  const { data, error } = adminApi.useLockStops(query, aborter.current.signal);
  const isInitialized = useInitializer(data || error);

  const updateVisibility = async (item: AdminLockStop, _visibility: "public" | "internal" | "hidden") => {
    try {
      await adminApi.updateLockStop(item.item.id, { visibility: _visibility });
      dispatch(showSuccessNotification(i18n.translation));
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  const [itemToDelete, setItemToDelete] = useState<AdminLockStop>();
  const [itemToEdit, setItemToEdit] = useState<AdminLockStop>();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const updateOrder = async (id: string, newOrder: number) => {
    try {
      await adminApi.updateLockStopOrder(id, { order: newOrder });
      dispatch(showSuccessNotification(i18n.translation));
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  if (!data && !error && !isInitialized) return null;

  return (
    <>
      <FlexContent padding>
        <Table<AdminLockStop>
          filters={filters}
          search={{
            placeholder: i18n.translation.common.search + "...",
            onChange: () => {
              aborter.current.abort();
              aborter.current = new AbortController();
            },
          }}
          headingActions={
            <Button onClick={() => setIsCreateOpen(true)}><Icon glyph={AddIcon} />
              {i18n.translation.common.add}
            </Button>
          }
          head={
            <TableRow>
              <TableHeadCell align="left">{i18n.translation.common.code}</TableHeadCell>
              <TableHeadCell align="left">{i18n.translation.lockCalculatorModule.admin.lockStops.fyK}</TableHeadCell>
              <TableHeadCell align="left">{i18n.translation.lockCalculatorModule.admin.lockStops.area}</TableHeadCell>
              <TableHeadCell align="left">{i18n.translation.lockCalculatorModule.admin.lockStops.fLockstopRk}</TableHeadCell>
              <TableHeadCell align="left">{i18n.translation.lockCalculatorModule.admin.lockStops.isSingle}</TableHeadCell>
              <TableHeadCell align="left">{i18n.translation.common.visibility}</TableHeadCell>
              <TableHeadCell align="center">{i18n.translation.common.order}</TableHeadCell>
              <TableHeadCell align="left"></TableHeadCell>
            </TableRow>
          }
          renderRow={(item) => (
            <>
              <TableCell>{item.item.code}</TableCell>
              <TableCell>{item.fyK != null ? item.fyK : i18n.translation.lockCalculatorModule.admin.emptyValue}</TableCell>
              <TableCell>{item.area != null ? item.area : i18n.translation.lockCalculatorModule.admin.emptyValue}</TableCell>
              <TableCell>{item.fLockstopRk != null ? item.fLockstopRk : i18n.translation.lockCalculatorModule.admin.emptyValue}</TableCell>
              <TableCell><BooleanValue value={item.isSingle} /></TableCell>
              <TableCell>
                {item.visibility != null && (
                  <Visibility
                    value={item.visibility as "public" | "internal" | "hidden"}
                    onChange={(nextVisibility) => updateVisibility(item, nextVisibility)}
                  />
                )}
              </TableCell>
              <TableCell align="center"><InlineOrderEditor order={item.order} onUpdate={(newOrder) => updateOrder(item.item.id, newOrder)} /></TableCell>
            </>
          )}
          onRowClick={(item) => setItemToEdit(item)}
          rowActions={(item) => [
            getEditAction(i18n, () => setItemToEdit(item)),
            getDeleteAction(i18n, () => setItemToDelete(item), true),
          ]}
          items={data?.items}
          total={data?.total}
          error={!!error}
        />

        {!!itemToDelete && (
          <ModalDialog
            onClose={() => setItemToDelete(undefined)}
            title={i18n.translation.lockCalculatorModule.admin.lockStops.singular}
            isSubmitting={isSubmitting}
            buttons={[<Button key={0} onClick={() => setItemToDelete(undefined)} variant="secondary">{i18n.translation.common.cancel}</Button>, <Button key={1} onClick={() => {
                  setIsSubmitting(true);
                  adminApi
                    .deleteLockStop(itemToDelete.item.id)
                    .then(() => {
                      dispatch(showSuccessNotification(i18n.translation));
                      setItemToDelete(undefined);
                    })
                    .catch((err) => dispatch(showErrorNotification(i18n, err)))
                    .finally(() => setIsSubmitting(false));
                }}>{i18n.translation.common.yes}</Button>]}
          >
            {i18n.translation.clients.questions.delete}
          </ModalDialog>
        )}
      </FlexContent>

      {isCreateOpen && <FormModal onClose={() => setIsCreateOpen(false)} />}
      {!!itemToEdit && <FormModal item={itemToEdit} onClose={() => setItemToEdit(undefined)} />}
    </>
  );
};

const FormModal: FC<{ item?: AdminLockStop; onClose: () => void }> = ({ item, onClose }) => {
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const adminApi = useAdminLockCalculatorApi();
  const itemsApi = useItemsApi();
  const [defaultOptions, setDefaultOptions] = useState<ItemOption[]>([]);
  const aborter = useRef(new AbortController());

  useEffect(() => {
    itemsApi
      .getAdminItems({ limit: 5, q: "" }, aborter.current.signal)
      .then((items) => setDefaultOptions(items.map((x) => ({ id: x.id, code: x.code, description: x.description }))))
      .catch(() => setDefaultOptions([]));
  }, []);

  const loadOptions = async (inputValue: string): Promise<ItemOption[]> => {
    aborter.current.abort();
    aborter.current = new AbortController();
    const items = await itemsApi.getAdminItems({ limit: 5, q: inputValue }, aborter.current.signal);
    return items.map((x) => ({ id: x.id, code: x.code, description: x.description }));
  };

  const defaultValues: FormBody = {
    item: item ? { id: item.item.id, code: item.item.code, description: item.item.description } : null,
    fyK: item?.fyK ?? null,
    area: item?.area ?? null,
    fLockstopRk: item?.fLockstopRk ?? null,
    isSingle: item?.isSingle ?? false,
    visibility: (item?.visibility as "public" | "internal" | "hidden") ?? "hidden",
  };

  const onSubmit = () => async (formData: FormBody) => {
    if (!formData.item) return;
    try {
      if (item) {
        await adminApi.updateLockStop(item.item.id, {
          fyK: formData.fyK || null,
          area: formData.area || null,
          fLockstopRk: formData.fLockstopRk || null,
          isSingle: formData.isSingle,
          visibility: formData.visibility,
        });
      } else {
        await adminApi.insertLockStop({
          itemId: formData.item.id,
          fyK: formData.fyK || null,
          area: formData.area || null,
          fLockstopRk: formData.fLockstopRk || null,
          isSingle: formData.isSingle,
          visibility: formData.visibility ?? "hidden",
        });
      }
      dispatch(showSuccessNotification(i18n.translation));
      onClose();
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  return (
    <ModalDialog
      onClose={onClose}
      title={item ? `${i18n.translation.common.edit} ${i18n.translation.lockCalculatorModule.admin.lockStops.singular}` : `${i18n.translation.common.add} ${i18n.translation.lockCalculatorModule.admin.lockStops.singular}`}
    >
      <Form<FormBody>
        submitText={i18n.translation.common.save}
        onSubmit={onSubmit}
       
        defaultValues={defaultValues}
        secondaryButton={
          <Button onClick={onClose} variant="secondary">
            {i18n.translation.common.cancel}
          </Button>
        }
      >
        {({ control }) => (
          <FormFieldsContainer fullWidth>
            <Controller
                name="item"
                control={control}
                rules={{ required: true }}
                render={({ field, fieldState }) => (
                  <FormField label={i18n.translation.common.code} error={fieldState.error}>
                    {({ labelId }) => (
                      <AsyncSelect<ItemOption, false>
                        {...field}
                        inputId={labelId}
                        defaultOptions={defaultOptions}
                        loadOptions={loadOptions}
                        isMulti={false}
                        isDisabled={!!item}
                        placeholder={`${i18n.translation.common.select}...`}
                        getOptionLabel={(x) => (x.description ? `${x.code} - ${x.description}` : x.code)}
                        getOptionValue={(x) => x.id}
                        value={defaultOptions.find((opt) => opt.id === field.value?.id) ?? field.value}
                        isInvalid={fieldState.invalid}
                        onChange={(option) => field.onChange(option)}
                      />
                    )}
                  </FormField>
                )}
              />
            <Controller
              name="fyK"
              control={control}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.lockCalculatorModule.admin.lockStops.fyK} error={fieldState.error}>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      id={labelId}
                      value={field.value ?? undefined}
                      decimalScale={2}
                      isInvalid={isInvalid}
                      onValueChange={(value) => field.onChange(value)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="area"
              control={control}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.lockCalculatorModule.admin.lockStops.area} error={fieldState.error}>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      id={labelId}
                      value={field.value ?? undefined}
                      decimalScale={2}
                      isInvalid={isInvalid}
                      onValueChange={(value) => field.onChange(value)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="fLockstopRk"
              control={control}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.lockCalculatorModule.admin.lockStops.fLockstopRk} error={fieldState.error}>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      id={labelId}
                      value={field.value ?? undefined}
                      decimalScale={2}
                      isInvalid={isInvalid}
                      onValueChange={(value) => field.onChange(value)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="isSingle"
              control={control}
              render={({ field }) => (
                <FormField label={i18n.translation.lockCalculatorModule.admin.lockStops.isSingle}>
                  {({ labelId }) => (
                    <Switch id={labelId} checked={field.value} onChange={(value) => field.onChange(value)} />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="visibility"
              control={control}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.visibility} error={fieldState.error}>
                  {() => <VisibilityScope value={field.value} onChange={(value) => field.onChange(value)} />}
                </FormField>
              )}
            />
          </FormFieldsContainer>
        )}
      </Form>
    </ModalDialog>
  );
};

export default AdminLockStopsTab;
